import { observable, makeObservable } from 'mobx'
import _ from 'lodash'
import axios from 'axios'
import { errorDetails } from 'constants.js'
import { removeHtml } from 'utils.js'
import { uiStore } from './UIStore'
import { logout, redirectToExpired, redirectToExpired24 } from '../utils'
import i18next from '../i18n'

const ERROR_BAR_HIDE_MS = 8000

// Stores the current error to show in the error bar,
// and allows the user to clear it
class ErrorStore {
  @observable errorMessage = null

  constructor () {
    makeObservable(this)
  }

  logError (message, e) {
    if (window.Rollbar) {
      window.Rollbar.error(message, e, e.response || e.request)
    } else {
      console.warn(message, e, e.response || e.request)
    }
  }

  error (e) {
    // Ignore cancelled requests
    if (axios.isCancel(e)) return

    // log user out if not authorized
    if (_.get(e, 'response.status') === 401) {
      return logout()
    }

    // log the user out if we get access denied from the API
    if (_.get(e, 'response.status') === 403) {
      if (_.get(e, 'response.data.detail') === errorDetails.EXPIRED) {
        return redirectToExpired()
      } else if (_.get(e, 'response.data.detail') === errorDetails.EXPIRED_24_TRIAL) {
        return redirectToExpired24()
      } else {
        return logout()
      }
    }

    // if error 404 after target group deleted ignore this error
    if (_.get(e, 'response.status') === 404 &&
      _.get(e, 'response.config.url').indexOf('api/geodata/target-group/') >= 0) {
      return
    }

    const status = _.get(e, 'response.status')
    const isSlowOrUnreachable = [502, 503, 504].includes(status) || (!e.response && Boolean(e.request))
    let error = isSlowOrUnreachable
      ? i18next.t('GLOBAL.REQUEST_TOO_SLOW')
      : i18next.t('GLOBAL.AN_ERROR_OCCURRED', { interpolation: { escapeValue: false } })

    // show specific error from response data
    if (_.get(e, 'response.status') === 400) {
      error = removeHtml(e.response.data[Object.keys(e.response.data)[0]][0])
      return uiStore.clear()
    }

    // show error only if it is not recognized
    this.errorMessage = error
    this.logError(error, e)
    clearTimeout(this.hideTimer)
    this.hideTimer = setTimeout(() => this.clear(), ERROR_BAR_HIDE_MS)
  }

  clear () {
    clearTimeout(this.hideTimer)
    this.errorMessage = null
  }
}

const errorStore = new ErrorStore()

// decorator which catches any errors thrown in a function
export function catchError (target, key, descriptor) {
  const original = descriptor.value
  descriptor.value = async function (...args) {
    try {
      return await original.apply(this, args)
    } catch (e) {
      errorStore.error(e)
    }
  }
  return descriptor
}

export default errorStore
